// src/pages/reports/profesores/HighPerformancePage.tsx
import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Stack,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Collapse,
  LinearProgress,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

// Hooks adaptadores (encolan job y hacen polling por ti)
import { useHpProfesores, useHpReservas, useHpReservaFirmas } from '../../../hooks/useHighPerf';

// TIPOS (desde la API)
import type { HpProfRow, HpReservaRow, HpFirmaDetRow } from '../../../api/reports';

type Totales = {
  horas: number;
  reservas: number;
  asignaturas: string[];
  aluFirma: number;
  aluForm: number;
  aluPend: number;
  aluTot: number;
};

export default function HighPerformancePage() {
  const [desde, setDesde] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [hasta, setHasta] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));
  const [submitted, setSubmitted] = useState<{ desde: string; hasta: string }>({
    desde,
    hasta,
  });
  const [profSel, setProfSel] = useState<number | null>(null);
  const [openRows, setOpenRows] = useState<Record<number, boolean>>({});

  const rangoValido = useMemo(
    () => dayjs(desde).isValid() && dayjs(hasta).isValid() && !dayjs(desde).isAfter(hasta),
    [desde, hasta],
  );

  /* ——— Profesores con High Performance en el periodo ——— */
  const { data: profes = [] as HpProfRow[], isFetching: loadingP } = useHpProfesores(
    submitted?.desde,
    submitted?.hasta,
  );

  /* Si cambia el periodo y el profe seleccionado ya no está, limpiamos selección */
  useEffect(() => {
    if (profSel && !profes.some((p: HpProfRow) => Number(p.profesorId) === Number(profSel))) {
      setProfSel(null);
      setOpenRows({});
    }
  }, [profes, profSel]);

  /* ——— Reservas HP del profesor seleccionado ——— */
  const { data: reservasRaw = [], isFetching: loadingR } = useHpReservas(
    profSel ?? undefined,
    submitted?.desde,
    submitted?.hasta,
  );

  // Forzamos el tipo estático de las reservas
  const reservas = (reservasRaw ?? []) as HpReservaRow[];

  const onBuscar = () => {
    if (!rangoValido) return;
    setSubmitted({ desde, hasta });
    setOpenRows({});
  };

  const toggleRow = (id: number) => setOpenRows((m) => ({ ...m, [id]: !m[id] }));

  /* Totales del profesor visible */
  const totales = useMemo<Totales>(() => {
    const init: Totales = {
      horas: 0,
      reservas: 0,
      aluFirma: 0,
      aluForm: 0,
      aluPend: 0,
      aluTot: 0,
      asignaturas: [],
    };

    if (!reservas.length) return init;

    return reservas.reduce((acc: Totales, r: HpReservaRow): Totales => {
      const nuevasAsignaturas = acc.asignaturas.includes(r.asignatura)
        ? acc.asignaturas
        : [...acc.asignaturas, r.asignatura];

      return {
        horas: acc.horas + (r.horas ?? 0),
        reservas: acc.reservas + 1,
        aluFirma: acc.aluFirma + (r.alumnosFirma ?? 0),
        aluForm: acc.aluForm + (r.alumnosFormulario ?? 0),
        aluPend: acc.aluPend + (r.alumnosSinFirma ?? 0),
        aluTot: acc.aluTot + (r.totalAlumnos ?? 0),
        asignaturas: nuevasAsignaturas,
      };
    }, init);
  }, [reservas]);

  return (
    <Box p={2}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        High Performance
      </Typography>

      {/* Filtros período */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            type="date"
            label="Desde"
            InputLabelProps={{ shrink: true }}
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
            error={!dayjs(desde).isValid()}
          />
          <TextField
            type="date"
            label="Hasta"
            InputLabelProps={{ shrink: true }}
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
            error={!dayjs(hasta).isValid() || dayjs(desde).isAfter(hasta)}
            helperText={!rangoValido ? 'Rango inválido' : undefined}
          />
          <Button variant="contained" onClick={onBuscar} disabled={!rangoValido}>
            Buscar
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={submitted.desde} size="small" />
            <Chip label={submitted.hasta} size="small" />
          </Stack>
        </Stack>
      </Paper>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        {/* Listado profesores */}
        <Paper sx={{ p: 1, width: { xs: '100%', md: 380 } }}>
          <Typography variant="subtitle2" sx={{ px: 1, py: 1 }}>
            Profesores
          </Typography>
          {loadingP && <LinearProgress />}
          <List dense disablePadding>
            {profes.map((p: HpProfRow) => (
              <ListItemButton
                key={p.profesorId}
                selected={Number(p.profesorId) === Number(profSel)}
                onClick={() => {
                  setProfSel(Number(p.profesorId));
                  setOpenRows({});
                }}
                sx={{ py: 1 }}
              >
                <ListItemText
                  primary={p.profesor}
                  secondary={
                    <span>
                      <Chip size="small" label={`${p.reservas} reservas`} sx={{ mr: 0.5 }} />
                      <Chip
                        size="small"
                        label={`${(p.horas ?? 0).toFixed(1)} h`}
                        sx={{ mr: 0.5 }}
                      />
                      <Chip size="small" label={`${p.alumnos} alumnos`} />
                    </span>
                  }
                />
              </ListItemButton>
            ))}
            {!loadingP && profes.length === 0 && (
              <Box sx={{ p: 2, color: 'text.secondary' }}>Sin resultados</Box>
            )}
          </List>
        </Paper>

        {/* Tabla de reservas del profesor seleccionado */}
        <Paper sx={{ p: 2, flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
              {profSel ? 'Reservas del profesor' : 'Selecciona un profesor'}
            </Typography>
            {/* Totales rápidos */}
            {profSel && reservas.length > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" label={`Total reservas: ${totales.reservas}`} />
                <Chip size="small" label={`Horas: ${totales.horas.toFixed(1)}`} />
                <Chip size="small" label={`Alumnos: ${totales.aluTot}`} />
                <Chip size="small" label={`Asignaturas: ${totales.asignaturas.join(', ')}`} />
                <Chip size="small" color="success" label={`firma: ${totales.aluFirma}`} />
                <Chip size="small" color="info" label={`form: ${totales.aluForm}`} />
                <Chip size="small" color="warning" label={`pend: ${totales.aluPend}`} />
              </Stack>
            )}
          </Stack>

          {/* Cabecera */}
          {profSel && (
            <Stack
              direction="row"
              sx={{ fontWeight: 600, color: 'text.secondary', px: 1, pb: 0.5 }}
              spacing={1}
            >
              <Box sx={{ width: 140 }}>Fecha</Box>
              <Box sx={{ width: 100 }}>Horas</Box>
              <Box sx={{ width: 140 }}>Alumnos</Box>
              <Box sx={{ width: 200 }}>Asignatura</Box>
              <Box sx={{ width: 240 }}>Firmas</Box>
              <Box sx={{ flex: 1 }} />
              <Box sx={{ width: 120, textAlign: 'right' }}>Detalle</Box>
            </Stack>
          )}

          {loadingR && <LinearProgress sx={{ mb: 1 }} />}

          {profSel &&
            reservas.map((r: HpReservaRow) => {
              const open = !!openRows[r.reservaId];
              return (
                <Box key={r.reservaId}>
                  <Stack direction="row" alignItems="center" sx={{ py: 1 }} spacing={1}>
                    <Box sx={{ width: 140 }}>{r.fecha}</Box>
                    <Box sx={{ width: 100 }}>{(r.horas ?? 0).toFixed(1)} h</Box>
                    <Box sx={{ width: 140 }}>
                      <Chip size="small" label={`Alumnos: ${r.totalAlumnos}`} />
                    </Box>
                    <Box sx={{ width: 200 }}>{r.asignatura}</Box>
                    <Box
                      sx={{
                        width: 240,
                        display: 'flex',
                        gap: 0.5,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Chip size="small" color="success" label={`firma: ${r.alumnosFirma}`} />
                      <Chip size="small" color="info" label={`form: ${r.alumnosFormulario}`} />
                      <Chip size="small" color="warning" label={`pend: ${r.alumnosSinFirma}`} />
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Button
                      size="small"
                      endIcon={open ? <ExpandLess /> : <ExpandMore />}
                      onClick={() => toggleRow(r.reservaId)}
                    >
                      Detalle
                    </Button>
                  </Stack>

                  <Collapse in={open} timeout="auto" unmountOnExit>
                    <DetalleFirmas reservaId={r.reservaId} />
                  </Collapse>

                  <Divider />
                </Box>
              );
            })}

          {profSel && !loadingR && reservas.length === 0 && (
            <Box sx={{ p: 2, color: 'text.secondary' }}>Sin reservas HP en este periodo</Box>
          )}
        </Paper>
      </Stack>

      {/* Leyenda */}
      <Box sx={{ mt: 2, color: 'text.secondary', fontSize: 13 }}>
        <strong>Leyenda:</strong>&nbsp;
        <Chip size="small" label="firma" color="success" sx={{ mr: 0.5 }} /> = firmado en App ·{' '}
        <Chip size="small" label="form" color="info" sx={{ mx: 0.5 }} /> = formulario de asistencia
        · <Chip size="small" label="pend" color="warning" sx={{ ml: 0.5 }} /> = sin firma ni
        formulario.
      </Box>
    </Box>
  );
}

function DetalleFirmas({ reservaId }: { reservaId: number }) {
  const { data: alumnos = [] as HpFirmaDetRow[], isFetching } = useHpReservaFirmas(reservaId);

  if (isFetching) return <LinearProgress sx={{ mb: 1 }} />;

  return (
    <Box sx={{ px: 2, pb: 1 }}>
      {alumnos.map((a: HpFirmaDetRow) => (
        <Stack key={a.alumnoId} direction="row" spacing={1} sx={{ py: 0.5 }}>
          <Box sx={{ width: 320 }}>{a.alumno}</Box>
          <Box>
            {a.firma && <Chip size="small" color="success" label="firma" sx={{ mr: 0.5 }} />}
            {a.formulario && <Chip size="small" color="info" label="form" sx={{ mr: 0.5 }} />}
            {!a.firma && !a.formulario && <Chip size="small" color="warning" label="pendiente" />}
          </Box>
        </Stack>
      ))}
      {alumnos.length === 0 && <Box sx={{ color: 'text.secondary' }}>Sin alumnos</Box>}
    </Box>
  );
}
